import type { GasVideoTrackingEvent } from '../../shared/kit';
import type { QuizFrontendResponse, QuizCheckResult, QuizAnswer, QuizSubmitResult } from './types';

/**
 * Client REST du player vidéo (gas/v1/video/*). Remplace les appels
 * admin-ajax.php des scripts legacy gas-video-stats.js.
 */
export interface VideoStatsApi {
    trackEvent(event: VideoTrackPayload): Promise<void>;
    trackBatch(events: VideoTrackPayload[]): Promise<void>;
    getResume(): Promise<number | null>;
}

export interface VideoTrackPayload {
    db_video_id: number;
    content_id: number;
    activite_id: number;
    event_type: GasVideoTrackingEvent['eventType'];
    current_time: number;
    duration_watched: number;
    video_duration: number;
    chapter_index?: number | null;
    chapter_title?: string | null;
    provider?: string;
}

// ─── Quiz API (cue points) ──────────────────────────────────────────────────

export interface QuizApi {
    loadQuestionsForGroup(groupId: number, locale?: string): Promise<QuizFrontendResponse | null>;
    checkAnswer(quizId: number, questionId: string, answer: QuizAnswer, quizNonce: string): Promise<QuizCheckResult | null>;
    submitQuiz(quizId: number, answers: QuizAnswer[], contentId: number, quizNonce: string): Promise<QuizSubmitResult | null>;
}

export function createQuizApi(restBaseUrl: string, wpNonce: string): QuizApi {
    const base = restBaseUrl.replace(/\/$/, '');
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(wpNonce ? { 'X-WP-Nonce': wpNonce } : {}),
    };

    async function get(path: string): Promise<unknown> {
        const res = await fetch(`${base}${path}`, { headers: { ...(wpNonce ? { 'X-WP-Nonce': wpNonce } : {}) } });
        if (!res.ok) {
            return null;
        }
        return res.json();
    }

    async function post(path: string, body: unknown): Promise<unknown> {
        const res = await fetch(`${base}${path}`, {
            method: 'POST',
            headers,
            body: JSON.stringify(body),
        });
        if (!res.ok) {
            return null;
        }
        return res.json();
    }

    return {
        async loadQuestionsForGroup(groupId, locale) {
            try {
                const params = locale ? `?locale=${encodeURIComponent(locale)}` : '';
                const json = await get(`/quiz/group/${groupId}/frontend${params}`) as { data?: QuizFrontendResponse } | null;
                return json?.data ?? null;
            } catch {
                return null;
            }
        },

        async checkAnswer(quizId, questionId, answer, quizNonce) {
            try {
                const { question_id: _qid, ...answerRest } = answer;
                const json = await post(`/quiz/${quizId}/check-answer`, {
                    quiz_id: quizId,
                    question_id: questionId,
                    nonce: quizNonce,
                    ...answerRest,
                }) as { data?: QuizCheckResult } | null;
                return json?.data ?? null;
            } catch {
                return null;
            }
        },

        async submitQuiz(quizId, answers, contentId, quizNonce) {
            try {
                const json = await post(`/quiz/${quizId}/submit`, {
                    quiz_id: quizId,
                    nonce: quizNonce,
                    answers,
                    content_id: contentId || undefined,
                    started_at: new Date().toISOString(),
                }) as { data?: QuizSubmitResult } | null;
                return json?.data ?? null;
            } catch {
                return null;
            }
        },
    };
}

export function createVideoStatsApi(restUrl: string, nonce: string, ids: {
    dbVideoId: number;
    contentId: number;
}): VideoStatsApi {
    const base = restUrl.replace(/\/$/, '');
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
    };
    if (nonce) {
        headers['X-WP-Nonce'] = nonce;
    }

    async function post(path: string, body: unknown): Promise<Response> {
        return fetch(`${base}${path}`, {
            method: 'POST',
            headers,
            body: JSON.stringify(body),
        });
    }

    return {
        async trackEvent(event) {
            try {
                await post('/stats/event', event);
            } catch {
                // Tracking best-effort : un échec réseau ne casse pas la lecture.
            }
        },
        async trackBatch(events) {
            if (events.length === 0) {
                return;
            }
            try {
                await post('/stats/batch', { events });
            } catch {
                // best-effort
            }
        },
        async getResume() {
            try {
                const params = new URLSearchParams();
                if (ids.contentId > 0) {
                    params.set('content_id', String(ids.contentId));
                }
                if (ids.dbVideoId > 0) {
                    params.set('video_id', String(ids.dbVideoId));
                }
                const res = await fetch(`${base}/stats/resume?${params.toString()}`, {
                    headers: nonce ? { 'X-WP-Nonce': nonce } : undefined,
                });
                if (!res.ok) {
                    return null;
                }
                const json = await res.json();
                const position = json?.data?.resume_position;
                return typeof position === 'number' ? position : null;
            } catch {
                return null;
            }
        },
    };
}
